import React, { useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { KTSVG, SimpleAlert } from '../../../../../rbts/helpers';
import { guardarRolService } from '../../../../service/catalogos/Roles';
import { useObtenerRolPorId } from '../hooks/RolesHook';

type Props = {
  show: boolean;
  handleClose: (updateGrid: boolean) => void;
  id_row: number;
};

const schema = Yup.object().shape({
  name: Yup.string().trim().required('El nombre es requerido.').max(50, 'Máximo 50 caracteres.'),
  code: Yup.string().trim().required('El código es requerido.').max(30, 'Máximo 30 caracteres.'),
  description: Yup.string().trim().nullable().max(100, 'Máximo 100 caracteres.'),
});

const CreateModal: React.FC<Props> = ({ id_row, show, handleClose }) => {
  const [loading, setLoading] = useState(false);
  const { initValues } = useObtenerRolPorId(id_row);

  const formik = useFormik({
    initialValues: initValues,
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        await guardarRolService(values);
        SimpleAlert(
          id_row ? 'Rol actualizado exitosamente.' : 'Rol creado exitosamente.',
          3000,
          'success'
        );
        handleClose(true);
        formik.resetForm();
      } catch (error: any) {
        const message = error?.response?.data?.message ?? 'Ocurrió un error.';
        SimpleAlert(message, 3000, 'error');
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal
      id="kt_modal_role"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered mw-500px"
      show={show}
      onHide={() => {
        formik.resetForm();
        handleClose(false);
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2>{id_row ? 'Editar rol' : 'Nuevo rol'}</h2>
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            onClick={() => {
              formik.resetForm();
              handleClose(false);
            }}
          >
            <KTSVG className="svg-icon-2" path="/media/icons/duotune/arrows/arr061.svg" />
          </div>
        </div>
        <div className="modal-body mx-5">
          <form onSubmit={formik.handleSubmit}>

            {/* Nombre */}
            <div className="fv-row mb-7">
              <div className="w-100 d-flex">
                <label className="required fw-bold fs-6 mb-2">Nombre</label>
                {formik.touched.name && formik.errors.name && (
                  <div className="fv-plugins-message-container text-danger ms-5">
                    <span role="alert">{formik.errors.name}</span>
                  </div>
                )}
              </div>
              <input
                placeholder="Nombre del rol"
                {...formik.getFieldProps('name')}
                className="form-control"
                type="text"
                autoComplete="off"
              />
            </div>

            {/* Código */}
            <div className="fv-row mb-7">
              <div className="w-100 d-flex">
                <label className="required fw-bold fs-6 mb-2">Código</label>
                {formik.touched.code && formik.errors.code && (
                  <div className="fv-plugins-message-container text-danger ms-5">
                    <span role="alert">{formik.errors.code}</span>
                  </div>
                )}
              </div>
              <input
                placeholder="Ej. ADMIN, CAJA, RETIRO"
                {...formik.getFieldProps('code')}
                className="form-control"
                type="text"
                autoComplete="off"
              />
            </div>

            {/* Descripción */}
            <div className="fv-row mb-7">
              <div className="w-100 d-flex">
                <label className="fw-bold fs-6 mb-2">Descripción (Opcional)</label>
                {formik.touched.description && formik.errors.description && (
                  <div className="fv-plugins-message-container text-danger ms-5">
                    <span role="alert">{formik.errors.description}</span>
                  </div>
                )}
              </div>
              <input
                placeholder="Descripción del rol"
                {...formik.getFieldProps('description')}
                className="form-control"
                type="text"
                autoComplete="off"
              />
            </div>

            <div className="text-center pt-10">
              <button
                type="reset"
                className="btn btn-light me-3"
                onClick={() => {
                  formik.resetForm();
                  handleClose(false);
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {!loading ? (
                  <span>{id_row ? 'Actualizar' : 'Guardar'}</span>
                ) : (
                  <span style={{ display: 'block' }}>
                    Espere por favor...
                    <span className="spinner-border spinner-border-sm align-middle ms-2" />
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export { CreateModal };
